import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Button } from '@adop/ui';
import { ImagePlus, Trash2 } from 'lucide-react';
import { ALLOWED_UPLOAD_MIME_TYPES, LIMITS } from '@adop/shared';
import type { UploadResponse } from '@adop/shared';
import { apiFetch, errorMessage } from '../lib/api';
import { mediaUrl } from '../lib/format';
import { Alert } from './Alert';

export interface ImageUploadProps {
  locationId: string;
  /** R2 object key. */
  value: string | null;
  onChange: (key: string | null) => void;
  label: string;
  accept?: string;
}

/** Uploads an image to R2 through the platform API and reports the stored key. */
export function ImageUpload({ locationId, value, onChange, label, accept }: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Prefer the URL the API just returned (an absolute CDN URL in production).
  const [uploaded, setUploaded] = useState<{ key: string; url: string } | null>(null);

  const preview = value && uploaded?.key === value ? uploaded.url : mediaUrl(value);

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > LIMITS.maxUploadBytes) {
      setError('Images must be 5 MB or smaller.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('location_id', locationId);
      const response = await apiFetch<UploadResponse>('/api/platform/uploads', {
        method: 'POST',
        body: form,
      });
      onChange(response.key);
      setUploaded({ key: response.key, url: response.url });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-zinc-900">{label}</p>
      <div className="flex items-center gap-3">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50">
          {preview ? (
            <img src={preview} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImagePlus className="h-6 w-6 text-zinc-400" aria-hidden="true" />
          )}
        </div>
        <div className="space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept={accept ?? ALLOWED_UPLOAD_MIME_TYPES.join(',')}
            className="hidden"
            onChange={handleFile}
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              loading={busy}
              onClick={() => inputRef.current?.click()}
            >
              {value ? 'Replace' : 'Upload'}
            </Button>
            {value ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onChange(null);
                  setUploaded(null);
                }}
              >
                <Trash2 className="h-4 w-4" /> Remove
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-zinc-500">JPEG, PNG, WebP or GIF. Max 5 MB.</p>
        </div>
      </div>
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}
